import { lazy, type ComponentType } from "react";

/**
 * True when an error looks like a failed dynamic import of an app chunk.
 * This happens when a tab stays open across a deploy: the old chunk file
 * names no longer exist on the server, so the import rejects.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    typeof error === "string"
      ? error
      : error instanceof Error
        ? `${error.name}: ${error.message}`
        : "";
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError|dynamically imported module/i.test(
    message,
  );
}

const RELOAD_FLAG = "lovable:chunk-reload";

/** Reload once per session so a stale tab picks up the new build. */
export function reloadOnceForNewBuild(): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return false;
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // Storage blocked: still reload, but only this once in this call.
  }
  window.location.reload();
  return true;
}

/** Clear the guard after a successful load so a later deploy can recover too. */
export function clearChunkReloadFlag() {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* ignore */
  }
}

/**
 * `React.lazy` that survives a deploy while the tab is open:
 * retry once, then reload the page once to fetch the new asset names.
 */
export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      clearChunkReloadFlag();
      return mod;
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;
      try {
        const mod = await factory();
        clearChunkReloadFlag();
        return mod;
      } catch (retryError) {
        if (isChunkLoadError(retryError) && reloadOnceForNewBuild()) {
          // Hold the promise while the browser navigates away.
          return await new Promise<{ default: T }>(() => {});
        }
        throw retryError;
      }
    }
  });
}
